import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDateISO } from '../utils';

interface WorkLogFilters {
  date: string;
  userId: string | null;
}

interface UseWorkLogFiltersUrlReturn {
  initialFilters: Partial<WorkLogFilters>;
  updateUrl: (filters: WorkLogFilters) => void;
}

export const useWorkLogFiltersUrl = (): UseWorkLogFiltersUrlReturn => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialFilters = useMemo((): Partial<WorkLogFilters> => {
    const params = new URLSearchParams(location.search);

    const filters: Partial<WorkLogFilters> = {};

    // Date
    const date = params.get('date');
    if (date) {
      filters.date = date;
    }

    // User ID
    const userId = params.get('userId');
    if (userId) {
      filters.userId = userId;
    }

    return filters;
  }, [location.search]);

  const updateUrl = useCallback(
    (filters: WorkLogFilters) => {
      const params = new URLSearchParams();
      const today = formatDateISO(new Date());

      // Date - only add if different from today
      if (filters.date && filters.date !== today) {
        params.set('date', filters.date);
      }

      // User ID - only add if specified
      if (filters.userId) {
        params.set('userId', filters.userId);
      }

      const search = params.toString();
      const newUrl = search ? `?${search}` : location.pathname;

      // Only update if URL actually changed
      if (location.search !== (search ? `?${search}` : '')) {
        navigate(newUrl, { replace: true });
      }
    },
    [location.pathname, location.search, navigate]
  );

  return { initialFilters, updateUrl };
};
